import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'outline';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
  iconRight?: ReactNode;
}

const variantClass: Record<Variant, string> = {
  primary:   'bg-amber text-ink hover:bg-amber-hover font-semibold',
  secondary: 'bg-white text-ink border border-border hover:bg-surface font-medium',
  ghost:     'bg-transparent text-muted hover:text-ink font-medium',
  outline:   'bg-white text-ink border border-ink hover:bg-surface font-semibold',
};
const sizeClass: Record<Size, string> = {
  sm: 'px-3 py-2 text-[13px] gap-1.5',
  md: 'px-4 py-2.5 text-sm gap-2',
  lg: 'px-5 py-3 text-[15px] gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon, iconRight, children, className = '', ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      className={[
        'inline-flex items-center justify-center rounded-btn transition-colors',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber focus-visible:outline-offset-2',
        variantClass[variant],
        sizeClass[size],
        className,
      ].join(' ')}
    >
      {icon}
      {children && <span>{children}</span>}
      {iconRight}
    </button>
  );
});
